import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import SubHeader from "components/common/SubHeader";
import LoadingSpinner from "components/common/LoadingSpiner";
import CommonModal from "components/common/CommonModal";
import BottomSheet from "components/common/BottomSheet";
import { useSnackbar } from "contexts/SnackbarContext";
import { matchesUserParam } from "utils/userIdentity";
import {
    PB_DISCIPLINES,
    isSecondsDiscipline,
    listPbHistory,
    updatePb,
    deletePb,
} from "api/freedivingPb";
import "assets/scss/pages/pbHistory.scss";

const MM_SS_REGEX = /^([0-9]{1,3}):([0-5][0-9])$/;

const PbHistory = () => {
    // URL 은 공개 식별자(public_id), PB API 는 내부 user_id 계약을 쓴다.
    const { userId: publicUserKey } = useParams();
    const userInfo = useUserStore((state) => state.userInfo);
    const { showError, showSuccess } = useSnackbar();

    // 본인 이력이면 세션의 내부 식별자로 번역한다. 타인 이력은 그대로 전달한다
    // (BE dual lookup 의존) — 번역은 public → internal 단방향이라 URL 에 내부 ID 가 새지 않는다.
    const apiUserId = matchesUserParam(userInfo, publicUserKey)
        ? userInfo.user_id
        : publicUserKey;

    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const canEdit = !!userInfo?.is_staff || !!userInfo?.is_admin;

    const load = useCallback(async () => {
        setIsLoading(true);
        const list = await listPbHistory(apiUserId);
        const arr = Array.isArray(list) ? list : list?.results ?? [];
        arr.sort((a, b) => (a.recorded_at < b.recorded_at ? 1 : -1));
        setRecords(arr);
        setIsLoading(false);
    }, [apiUserId]);

    useEffect(() => {
        if (!apiUserId) return;
        load();
    }, [apiUserId, load]);

    const groupedRecords = useMemo(() => {
        const byCode = new Map();
        records.forEach((r) => {
            if (!byCode.has(r.discipline)) byCode.set(r.discipline, []);
            byCode.get(r.discipline).push(r);
        });
        return PB_DISCIPLINES
            .filter((d) => byCode.has(d.code))
            .map((d) => {
                const items = byCode.get(d.code);
                const isSeconds = isSecondsDiscipline(d.code);
                const bestRecord = items.reduce((best, cur) => {
                    if (!best) return cur;
                    const curVal = isSeconds
                        ? cur.value_seconds
                        : Number(cur.value_meters);
                    const bestVal = isSeconds
                        ? best.value_seconds
                        : Number(best.value_meters);
                    if (isSeconds) return curVal > bestVal ? cur : best;
                    return curVal > bestVal ? cur : best;
                }, null);
                return {
                    discipline: d.code,
                    label: d.label,
                    unitSuffix: isSeconds ? "mm:ss" : "m",
                    bestRecord,
                    items,
                };
            });
    }, [records]);

    const openEdit = (rec) => {
        setEditTarget(rec);
        setEditForm({
            discipline: rec.discipline,
            value_meters: rec.value_meters ?? "",
            value_seconds: rec.value_seconds ?? "",
            recorded_at: rec.recorded_at,
            note: rec.note || "",
        });
    };

    const closeEdit = () => {
        setEditTarget(null);
        setEditForm(null);
    };

    const submitEdit = async () => {
        if (!editForm || !editTarget || isSaving) return;
        const isSeconds = isSecondsDiscipline(editForm.discipline);

        if (isSeconds) {
            if (!editForm.value_seconds || !MM_SS_REGEX.test(editForm.value_seconds)) {
                showError("STA 기록은 mm:ss 형식(예: 06:30)으로 입력해주세요");
                return;
            }
        } else {
            const num = Number(editForm.value_meters);
            if (!editForm.value_meters || isNaN(num) || num <= 0) {
                showError("거리를 입력해주세요");
                return;
            }
            if (num > 999.9) {
                showError("거리는 최대 999.9m까지 입력 가능합니다");
                return;
            }
        }
        if (!editForm.recorded_at) {
            showError("측정일을 입력해주세요");
            return;
        }

        const payload = {
            discipline: editForm.discipline,
            recorded_at: editForm.recorded_at,
            note: editForm.note || "",
        };
        if (isSeconds) {
            payload.value_seconds = editForm.value_seconds;
            payload.value_meters = null;
        } else {
            payload.value_meters = Number(editForm.value_meters).toFixed(1);
            payload.value_seconds = null;
        }

        try {
            setIsSaving(true);
            await updatePb(editTarget.id, payload);
            showSuccess("기록을 수정했습니다");
            closeEdit();
            await load();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                "기록 수정에 실패했습니다";
            showError(typeof msg === "string" ? msg : "기록 수정에 실패했습니다");
        } finally {
            setIsSaving(false);
        }
    };

    const submitDelete = async () => {
        if (!deleteTarget || isDeleting) return;
        try {
            setIsDeleting(true);
            await deletePb(deleteTarget.id);
            showSuccess("기록을 삭제했습니다");
            setDeleteTarget(null);
            await load();
        } catch {
            showError("기록 삭제에 실패했습니다");
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="pb-history-page">
            <SubHeader title="PB 기록 이력" />

            {groupedRecords.length === 0 ? (
                <div className="pb-history-empty">등록된 기록이 없습니다</div>
            ) : (
                <div className="pb-history-groups">
                    {groupedRecords.map((group) => (
                        <section key={group.discipline} className="pb-history-group">
                            <header className="pb-history-group__header">
                                <span className="pb-history-group__label">
                                    {group.label}
                                </span>
                                <span className="pb-history-group__best">
                                    최고 {group.bestRecord?.value_display ?? `- ${group.unitSuffix}`}
                                </span>
                                <span className="pb-history-group__count">
                                    {group.items.length}건
                                </span>
                            </header>
                            <ul className="pb-history-list">
                                {group.items.map((r) => {
                                    const isBest = r.id === group.bestRecord?.id;
                                    return (
                                        <li key={r.id} className="pb-history-item">
                                            <div className="pb-history-item__main">
                                                <span className="pb-history-item__value">
                                                    {r.value_display}
                                                </span>
                                                {isBest && (
                                                    <span className="pb-history-item__best-badge">
                                                        BEST
                                                    </span>
                                                )}
                                                <span className="pb-history-item__date">
                                                    {r.recorded_at}
                                                </span>
                                            </div>
                                            {canEdit && r.note && (
                                                <p className="pb-history-item__note">
                                                    {r.note}
                                                </p>
                                            )}
                                            {canEdit && (
                                                <div className="pb-history-item__actions">
                                                    <button
                                                        type="button"
                                                        className="pb-history-item__btn"
                                                        onClick={() => openEdit(r)}
                                                    >
                                                        수정
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="pb-history-item__btn pb-history-item__btn--danger"
                                                        onClick={() => setDeleteTarget(r)}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    ))}
                </div>
            )}

            <BottomSheet
                isOpen={!!editTarget}
                onClose={closeEdit}
                title="기록 수정"
                height="auto"
                className="pb-input-sheet"
            >
                {editForm && (
                    <div className="pb-input-form">
                        <div className="pb-input-row">
                            <label className="pb-input-label">종목</label>
                            <select
                                className="pb-input-select"
                                value={editForm.discipline}
                                onChange={(e) =>
                                    setEditForm((p) => ({ ...p, discipline: e.target.value }))
                                }
                            >
                                {PB_DISCIPLINES.map((d) => (
                                    <option key={d.code} value={d.code}>
                                        {d.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="pb-input-row">
                            <label className="pb-input-label">
                                기록 {isSecondsDiscipline(editForm.discipline) ? "(mm:ss)" : "(m)"}
                            </label>
                            {isSecondsDiscipline(editForm.discipline) ? (
                                <input
                                    className="pb-input-text"
                                    type="text"
                                    placeholder="06:30"
                                    value={editForm.value_seconds}
                                    onChange={(e) =>
                                        setEditForm((p) => ({
                                            ...p,
                                            value_seconds: e.target.value,
                                        }))
                                    }
                                />
                            ) : (
                                <input
                                    className="pb-input-text"
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="999.9"
                                    placeholder="35.5"
                                    value={editForm.value_meters}
                                    onChange={(e) =>
                                        setEditForm((p) => ({
                                            ...p,
                                            value_meters: e.target.value,
                                        }))
                                    }
                                />
                            )}
                        </div>

                        <div className="pb-input-row">
                            <label className="pb-input-label">측정일</label>
                            <input
                                className="pb-input-text"
                                type="date"
                                value={editForm.recorded_at}
                                onChange={(e) =>
                                    setEditForm((p) => ({ ...p, recorded_at: e.target.value }))
                                }
                            />
                        </div>

                        <div className="pb-input-row">
                            <label className="pb-input-label">메모 (선택)</label>
                            <input
                                className="pb-input-text"
                                type="text"
                                placeholder="예) 풀 환경, 컨디션 등"
                                value={editForm.note}
                                onChange={(e) =>
                                    setEditForm((p) => ({ ...p, note: e.target.value }))
                                }
                            />
                        </div>

                        <div className="pb-input-actions">
                            <button
                                type="button"
                                className="btn btn-gray200 btn-fit"
                                onClick={closeEdit}
                                disabled={isSaving}
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                className="btn btn-purple500 btn-fit"
                                onClick={submitEdit}
                                disabled={isSaving}
                            >
                                {isSaving ? "저장 중..." : "저장"}
                            </button>
                        </div>
                    </div>
                )}
            </BottomSheet>

            <CommonModal
                isOpen={!!deleteTarget}
                onRequestClose={() => setDeleteTarget(null)}
                className="pb-history-delete-modal"
            >
                <div className="pb-history-delete-body">
                    <p>이 기록을 삭제하시겠습니까?</p>
                    <div className="pb-history-delete-actions">
                        <button
                            type="button"
                            className="btn btn-gray200 btn-fit"
                            onClick={() => setDeleteTarget(null)}
                            disabled={isDeleting}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="btn btn-purple500 btn-fit"
                            onClick={submitDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? "삭제 중..." : "삭제"}
                        </button>
                    </div>
                </div>
            </CommonModal>
        </div>
    );
};

export default PbHistory;
